import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { dashboardCustomizationService } from '../../../services/dashboardCustomizationService';
import { rbacService } from '../../../services/rbacService';

const DashboardCustomizationModal = ({ userEmail, availableWidgets, onClose, onSave }) => {
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [draggedWidget, setDraggedWidget] = useState(null);
  const [userRoles, setUserRoles] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load user roles
      const roles = await rbacService?.getUserRoleAssignments(userEmail);
      setUserRoles(roles);

      // Load user preferences
      const preferences = await dashboardCustomizationService?.getUserWidgetPreferences(userEmail);
      const preferencesMap = new Map(
        preferences?.map(pref => [pref?.widget?.widgetType, pref])
      );

      // Filter widgets by role permissions
      const accessibleWidgets = [];
      for (const widget of availableWidgets) {
        if (!widget?.requiredModule) {
          accessibleWidgets?.push(widget);
          continue;
        }

        try {
          const hasAccess = await rbacService?.checkUserPermission(
            userEmail,
            widget?.requiredModule,
            'read'
          );
          if (hasAccess) {
            accessibleWidgets?.push(widget);
          }
        } catch (error) {
          console.error(`Error checking permission for ${widget?.id}:`, error);
        }
      }

      // Apply user preferences or defaults
      const widgetsWithPreferences = accessibleWidgets?.map((widget, index) => {
        const pref = preferencesMap?.get(widget?.id);
        return {
          ...widget,
          isVisible: pref?.isVisible ?? true,
          positionOrder: pref?.positionOrder ?? index + 1
        };
      });

      // Sort by position order
      widgetsWithPreferences?.sort((a, b) => a?.positionOrder - b?.positionOrder);

      setWidgets(widgetsWithPreferences);
    } catch (err) {
      setError(err?.message || 'Failed to load widgets');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleVisibility = (widgetId) => {
    setWidgets(prev => prev?.map(w => 
      w?.id === widgetId ? { ...w, isVisible: !w?.isVisible } : w
    ));
  };

  const handleDragStart = (e, widget) => {
    setDraggedWidget(widget);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetWidget) => {
    e?.preventDefault();
    
    if (!draggedWidget || draggedWidget?.id === targetWidget?.id) return;

    const draggedIndex = widgets?.findIndex(w => w?.id === draggedWidget?.id);
    const targetIndex = widgets?.findIndex(w => w?.id === targetWidget?.id);

    const newWidgets = [...widgets];
    newWidgets?.splice(draggedIndex, 1);
    newWidgets?.splice(targetIndex, 0, draggedWidget);

    // Update position order
    const reordered = newWidgets?.map((w, index) => ({
      ...w,
      positionOrder: index + 1
    }));

    setWidgets(reordered);
    setDraggedWidget(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      // Save each widget preference
      await Promise.all(
        widgets?.map(widget => 
          dashboardCustomizationService?.saveUserWidgetPreference(
            userEmail,
            widget?.id,
            {
              isVisible: widget?.isVisible,
              positionOrder: widget?.positionOrder
            }
          )
        )
      );

      onSave?.();
      onClose?.();
    } catch (err) {
      setError(err?.message || 'Failed to save customization');
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefault = () => {
    const resetWidgets = widgets?.map((widget, index) => ({
      ...widget,
      isVisible: true,
      positionOrder: index + 1
    }));
    setWidgets(resetWidgets);
  };

  const getWidgetIcon = (icon) => {
    return icon || 'Layout';
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Workforce Management': 'bg-blue-500/10 text-blue-500',
      'Payroll & Compliance': 'bg-green-500/10 text-green-500',
      'Operations': 'bg-purple-500/10 text-purple-500',
      'Analytics': 'bg-orange-500/10 text-orange-500'
    };
    return colors?.[category] || 'bg-muted text-muted-foreground';
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-card rounded-lg shadow-xl max-w-4xl w-full p-6">
          <div className="flex items-center justify-center py-8">
            <Icon name="Loader" size={32} color="var(--color-muted-foreground)" className="animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
              <Icon name="Layout" size={24} />
              Customize Your Dashboard
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Drag to reorder, toggle visibility, and personalize your workspace
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-md mb-4 flex items-start gap-2">
              <Icon name="AlertCircle" size={18} className="mt-0.5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Info Banner */}
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-primary mt-0.5" />
              <div className="flex-1">
                <h3 className="font-medium text-foreground mb-1">Role-Based Access</h3>
                <p className="text-sm text-muted-foreground">
                  Widgets shown below are filtered based on your role permissions. 
                  Hidden widgets require additional access rights.
                </p>
              </div>
            </div>
          </div>

          {/* Widgets List */}
          <div className="space-y-2">
            {widgets?.map((widget) => (
              <div
                key={widget?.id}
                draggable
                onDragStart={(e) => handleDragStart(e, widget)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, widget)}
                className={`
                  bg-muted/30 rounded-lg p-4 cursor-move transition-all
                  hover:bg-muted/50 hover:shadow-md
                  ${draggedWidget?.id === widget?.id ? 'opacity-50 scale-95' : ''}
                  ${!widget?.isVisible ? 'opacity-60' : ''}
                `}
              >
                <div className="flex items-center gap-4">
                  {/* Drag Handle */}
                  <Icon name="GripVertical" size={20} color="var(--color-muted-foreground)" />
                  
                  {/* Widget Icon */}
                  <div className={`
                    w-12 h-12 rounded-md flex items-center justify-center
                    ${widget?.isVisible ? 'bg-primary/10' : 'bg-muted'}
                  `}>
                    <Icon 
                      name={getWidgetIcon(widget?.icon)} 
                      size={24} 
                      color={widget?.isVisible ? 'var(--color-primary)' : 'var(--color-muted-foreground)'} 
                    />
                  </div>

                  {/* Widget Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-foreground">{widget?.title}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded ${getCategoryColor(widget?.category)}`}>
                        {widget?.category}
                      </span>
                      {widget?.priority === 'high' && (
                        <span className="text-xs bg-error/10 text-error px-2 py-0.5 rounded">
                          High Priority
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{widget?.description}</p>
                  </div>

                  {/* Visibility Toggle */}
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={widget?.isVisible}
                      onChange={() => handleToggleVisibility(widget?.id)}
                    />
                    <span className="text-sm text-muted-foreground">
                      {widget?.isVisible ? 'Visible' : 'Hidden'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {widgets?.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <Icon name="Layout" size={48} className="mx-auto mb-3 opacity-50" />
              <p className="text-sm">No widgets available for your role</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-6 border-t border-border bg-muted/20">
          <div className="flex items-center gap-4">
            <p className="text-xs text-muted-foreground">
              {widgets?.filter(w => w?.isVisible)?.length} of {widgets?.length} widgets visible
            </p>
            <button
              onClick={handleResetToDefault}
              className="text-xs text-primary hover:underline"
            >
              Reset to Default
            </button>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              iconName="Save"
              onClick={handleSave}
              loading={saving}
            >
              Save Layout
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardCustomizationModal;